import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { format } from "date-fns";
import {
  AlertCircle,
  AtSign,
  CalendarIcon,
  Camera,
  Check,
  Eye,
  EyeOff,
  Gift,
  Loader2,
  Mail,
  Phone,
  Shirt,
  UserRound,
  X,
} from "lucide-react";

import { Header } from "@/components/Header";
import logo from "@/assets/logo.svg";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SegmentedOtpInput } from "@/components/ui/segmented-otp-input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useAuth } from "@/lib/auth";
import { useDevState } from "@/lib/dev-state";
import type { PhoneSignInChallenge, SignUpInput } from "@/lib/firebase/auth";
import { navigateAfterAuth, usePostAuthRedirectParam } from "@/lib/post-auth-redirect";
import { toastError } from "@/lib/toast";

const getLocationContext = () => {
  if (typeof window === "undefined") return {};
  return {
    hostname: window.location.hostname,
    origin: window.location.origin,
  };
};

export const Route = createFileRoute("/signup")({
  head: () => ({
    meta: [
      { title: "Create your account — Real Pranks" },
      {
        name: "description",
        content:
          "Join Real Pranks. Create an account with email, Google, Apple, or your phone number.",
      },
      { property: "og:title", content: "Create your account — Real Pranks" },
      {
        property: "og:description",
        content: "Join Real Pranks today.",
      },
    ],
  }),
  component: SignupPage,
});

const T_SHIRT_SIZES = ["XS", "S", "M", "L", "XL", "XXL", "XXXL"];

const MIN_AGE = 13;

function isAtLeast(age: number, dob: Date): boolean {
  const today = new Date();
  const ageDate = new Date(
    today.getFullYear() - age,
    today.getMonth(),
    today.getDate(),
  );
  return dob <= ageDate;
}

type SharedProfile = {
  name: string;
  dob: Date | undefined;
  gender: "male" | "female" | "";
  phoneNumber: string;
  instagram: string;
  tShirt: string;
  avatarFile: File | null;
};

const initialShared: SharedProfile = {
  name: "",
  dob: undefined,
  gender: "",
  phoneNumber: "",
  instagram: "",
  tShirt: "",
  avatarFile: null,
};

function buildExtras(shared: SharedProfile): Partial<SignUpInput> {
  return {
    name: shared.name.trim(),
    phoneNumber: shared.phoneNumber.trim() || undefined,
    instagram: shared.instagram.trim() || undefined,
    gender: shared.gender || undefined,
    tShirt: shared.tShirt || undefined,
    birthDay: shared.dob?.getDate(),
    birthMonth: shared.dob ? shared.dob.getMonth() + 1 : undefined,
    birthYear: shared.dob?.getFullYear(),
    avatarFile: shared.avatarFile ?? undefined,
  };
}

function validateShared(shared: SharedProfile): string | null {
  if (!shared.name.trim()) return "Name is required.";
  if (!shared.dob) return "Date of birth is required.";
  if (!isAtLeast(MIN_AGE, shared.dob))
    return `You must be at least ${MIN_AGE} years old to sign up.`;
  if (!shared.gender) return "Gender is required.";
  return null;
}

function SignupPage() {
  const navigate = useNavigate();
  const redirect = usePostAuthRedirectParam();
  const {
    signUp,
    signInWithGoogle,
    signInWithApple,
    startPhoneSignIn,
    signedIn,
    pendingAccountMerge,
    clearAccountMerge,
  } = useAuth();
  const { state: devState, setState: setDevState } = useDevState();

  const [shared, setShared] = useState<SharedProfile>(initialShared);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Inline validation message for the shared profile section. Set when the
  // user tries to submit (any method) with missing required fields, so the
  // error is visible next to the fields instead of only in a transient toast.
  const [sharedError, setSharedError] = useState<string | null>(null);
  const profileSectionRef = useRef<HTMLElement>(null);

  // Email/password
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);

  // Social
  const [googleLoading, setGoogleLoading] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);

  // Phone
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [phoneLoading, setPhoneLoading] = useState(false);
  const [challenge, setChallenge] = useState<PhoneSignInChallenge | null>(null);
  const recaptchaRawId = useId();
  const recaptchaIdRef = useRef(
    `recaptcha-signup-${recaptchaRawId.replace(/[:]/g, "")}`,
  );
  const pendingEmailVerifyRef = useRef(false);

  useEffect(() => {
    if (signedIn && !pendingEmailVerifyRef.current) navigateAfterAuth(navigate, redirect);
  }, [signedIn, navigate, redirect]);

  useEffect(() => {
    if (!shared.avatarFile) {
      setAvatarPreview(null);
      return;
    }
    const url = URL.createObjectURL(shared.avatarFile);
    setAvatarPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [shared.avatarFile]);

  const dobFromYear = useMemo(() => new Date(1900, 0, 1), []);
  const dobToDate = useMemo(() => new Date(), []);

  const onPickAvatar = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toastError("Please choose an image file.", { flow: "signup-avatar-validate" });
      return;
    }
    setShared((s) => ({ ...s, avatarFile: file }));
  };

  const update = <K extends keyof SharedProfile>(key: K, value: SharedProfile[K]) => {
    setSharedError(null);
    setShared((s) => ({ ...s, [key]: value }));
  };

  /** Validates the shared profile; shows the error inline and scrolls to it. */
  const ensureSharedValid = (flow: string): boolean => {
    const err = validateShared(shared);
    if (!err) {
      setSharedError(null);
      return true;
    }
    setSharedError(err);
    profileSectionRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
    toastError(err, { flow });
    return false;
  };

  // ---------------- Email submit ----------------
  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ensureSharedValid("signup-email-validate-shared")) return;
    if (!email.includes("@")) {
      toastError("Please enter a valid email.", { flow: "signup-email-validate" });
      return;
    }
    if (password.length < 8) {
      toastError("Password must be at least 8 characters.", {
        flow: "signup-password-validate",
      });
      return;
    }
    setEmailLoading(true);
    pendingEmailVerifyRef.current = true;
    const result = await signUp({
      email,
      password,
      ...buildExtras(shared),
    });
    setEmailLoading(false);
    if ("error" in result) {
      pendingEmailVerifyRef.current = false;
      toastError(result.error, { flow: "signup-email-submit" });
      return;
    }
    if (devState !== "real") setDevState("real");
    toast.success(`Account created. We sent a verification link to ${email}.`);
    navigate({
      to: "/verify-email",
      search: { email, redirect: redirect ?? "/membership" },
    });
  };

  // ---------------- Social ----------------
  const handleGoogleSignUp = async () => {
    if (!ensureSharedValid("signup-google-validate-shared")) return;
    setGoogleLoading(true);
    const result = await signInWithGoogle(buildExtras(shared));
    setGoogleLoading(false);
    if ("error" in result) {
      toastError(result.error, { flow: "signup-google-submit" });
      return;
    }
    if (devState !== "real") setDevState("real");
    toast.success("Account created. Welcome!");
    navigateAfterAuth(navigate, redirect ?? "/membership");
  };

  const handleAppleSignUp = async () => {
    if (!ensureSharedValid("signup-apple-validate-shared")) return;
    setAppleLoading(true);
    const result = await signInWithApple(buildExtras(shared));
    setAppleLoading(false);
    if ("error" in result) {
      toastError(result.error, { flow: "signup-apple-submit" });
      return;
    }
    if (devState !== "real") setDevState("real");
    toast.success("Account created. Welcome!");
    navigateAfterAuth(navigate, redirect ?? "/membership");
  };

  // ---------------- Phone ----------------
  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ensureSharedValid("signup-phone-validate-shared")) return;
    if (!/^\+\d{6,}$/.test(phone)) {
      toastError("Enter your phone number in international format (e.g. +15551234567).", {
        flow: "signup-phone-format",
      });
      return;
    }
    setPhoneLoading(true);
    const result = await startPhoneSignIn(phone, recaptchaIdRef.current, {
      ...buildExtras(shared),
      phoneNumber: phone,
    });
    setPhoneLoading(false);
    if ("error" in result) {
      toastError(result.error, {
        flow: "signup-phone-send-code",
        ...getLocationContext(),
      });
      return;
    }
    setChallenge(result.challenge);
    toast.success("Code sent. Check your messages.");
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!challenge) return;
    if (!/^\d{4,8}$/.test(code)) {
      toastError("Enter the verification code.", { flow: "signup-phone-code-validate" });
      return;
    }
    setPhoneLoading(true);
    const result = await challenge.confirm(code);
    setPhoneLoading(false);
    if ("error" in result) {
      toastError(result.error, {
        flow: "signup-phone-verify-code",
        ...getLocationContext(),
      });
      return;
    }
    if (devState !== "real") setDevState("real");
    toast.success("Account created. Welcome!");
    navigateAfterAuth(navigate, redirect ?? "/membership");
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <main className="mx-auto flex w-full max-w-[92%] flex-col gap-6 px-4 py-8 sm:max-w-xl">
        <img src={logo} alt="" className="mx-auto h-16 w-auto pt-4" />
        <div className="text-center">
          <h1 className="text-[1.4rem] font-bold leading-tight">Create Account</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Join Real Pranks. We just need a few details.
          </p>
        </div>

        {/* Shared profile fields */}
        <section
          ref={profileSectionRef}
          className="flex scroll-mt-24 flex-col gap-5 rounded-xl border border-border bg-card p-5 text-card-foreground"
        >
          <div className="flex items-center gap-3">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
              1
            </span>
            <h2 className="flex items-center gap-2 text-lg font-semibold">
              <UserRound className="h-5 w-5 text-muted-foreground" />
              Your profile
            </h2>
          </div>

          {sharedError && (
            <div
              role="alert"
              className="flex items-start gap-2.5 rounded-lg border border-destructive/50 bg-destructive/10 px-3 py-2.5 text-sm text-destructive"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <p>{sharedError}</p>
            </div>
          )}

          {/* Avatar */}
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="group relative inline-flex h-20 w-20 items-center justify-center overflow-hidden rounded-full border border-border bg-secondary text-muted-foreground transition-colors hover:bg-accent"
              aria-label="Upload profile picture"
            >
              {avatarPreview ? (
                <img
                  src={avatarPreview}
                  alt="Selected avatar"
                  className="h-full w-full object-cover"
                />
              ) : (
                <Camera className="h-7 w-7" />
              )}
            </button>
            <div className="flex flex-col gap-1">
              <p className="text-sm font-medium">Profile picture</p>
              <p className="text-xs text-muted-foreground">
                Optional. PNG or JPG, square works best.
              </p>
              {shared.avatarFile && (
                <button
                  type="button"
                  onClick={() => update("avatarFile", null)}
                  className="inline-flex w-fit items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3 w-3" /> Remove
                </button>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={onPickAvatar}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="name">
                Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="name"
                required
                autoComplete="name"
                value={shared.name}
                onChange={(e) => update("name", e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>
                Date of birth <span className="text-destructive">*</span>
              </Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    type="button"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !shared.dob && "text-muted-foreground",
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4 opacity-70" />
                    {shared.dob ? format(shared.dob, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={shared.dob}
                    onSelect={(d) => update("dob", d)}
                    captionLayout="dropdown"
                    startMonth={dobFromYear}
                    endMonth={dobToDate}
                    defaultMonth={shared.dob ?? new Date(2000, 0, 1)}
                    disabled={(date) => date > dobToDate || date < dobFromYear}
                    autoFocus
                    className={cn("p-3 pointer-events-auto")}
                  />
                </PopoverContent>
              </Popover>
              <p className="text-xs text-muted-foreground">
                You must be at least {MIN_AGE} to sign up.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="gender">
                Gender <span className="text-destructive">*</span>
              </Label>
              <Select
                value={shared.gender}
                onValueChange={(v) => update("gender", v as SharedProfile["gender"])}
              >
                <SelectTrigger id="gender">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="profilePhone" className="flex items-center gap-1.5">
                <Phone className="h-3.5 w-3.5 text-muted-foreground" />
                Phone <span className="text-muted-foreground">(optional)</span>
              </Label>
              <Input
                id="profilePhone"
                type="tel"
                autoComplete="tel"
                placeholder="+15551234567"
                value={shared.phoneNumber}
                onChange={(e) => update("phoneNumber", e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                So we can reach you if you win a giveaway.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="instagram" className="flex items-center gap-1.5">
                <AtSign className="h-3.5 w-3.5 text-muted-foreground" />
                Instagram <span className="text-muted-foreground">(optional)</span>
              </Label>
              <Input
                id="instagram"
                placeholder="@yourhandle"
                value={shared.instagram}
                onChange={(e) => update("instagram", e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                So creators can tag you in shout-outs.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="tshirt" className="flex items-center gap-1.5">
                <Shirt className="h-3.5 w-3.5 text-muted-foreground" />
                T-shirt size <span className="text-muted-foreground">(optional)</span>
              </Label>
              <Select
                value={shared.tShirt}
                onValueChange={(v) => update("tShirt", v)}
              >
                <SelectTrigger id="tshirt">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {T_SHIRT_SIZES.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="flex items-center gap-1 text-xs text-muted-foreground">
                <Gift className="h-3 w-3" />
                For merch drops and prizes.
              </p>
            </div>
          </div>
        </section>

        {/* Sign-up method */}
        <section className="flex flex-col gap-4 rounded-xl border border-border bg-card p-5 text-card-foreground">
          <div className="flex items-center gap-3">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
              2
            </span>
            <h2 className="text-lg font-semibold">
              How would you like to sign up?
            </h2>
          </div>

          <div className="flex flex-col gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={handleGoogleSignUp}
              disabled={googleLoading || appleLoading}
              className="h-11 gap-3 border-[#dadce0] bg-white text-[14px] font-medium text-[#1f1f1f] hover:bg-[#f8f9fa] hover:text-[#1f1f1f] dark:border-border dark:bg-background dark:text-foreground dark:hover:bg-accent dark:hover:text-foreground"
            >
              {googleLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <GoogleIcon className="h-5 w-5 shrink-0" />
              )}
              Sign up with Google
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleAppleSignUp}
              disabled={googleLoading || appleLoading}
              className="h-11 gap-3 border-border bg-background text-[14px] font-medium hover:bg-accent"
            >
              {appleLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <AppleIcon className="h-5 w-5 shrink-0" />
              )}
              Sign up with Apple
            </Button>
          </div>
          {pendingAccountMerge && (
            <div className="rounded-lg border border-amber-300/50 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
              <p className="font-medium">This email already has an account</p>
              <p className="mt-1 text-xs">
                Sign in with your existing method first, then merge {pendingAccountMerge.providerId === "apple.com" ? "Apple" : "Google"} from account security.
              </p>
              <div className="mt-2 flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="default"
                  onClick={() =>
                    redirect
                      ? void navigate({ to: "/login", search: { redirect } })
                      : void navigate({ to: "/login" })
                  }
                >
                  Merge now
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={clearAccountMerge}
                >
                  Not now
                </Button>
              </div>
            </div>
          )}

          <div className="relative my-1">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">or</span>
            </div>
          </div>

          <Tabs defaultValue="email" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="email">
                <Mail className="mr-2 h-4 w-4" />
                Email
              </TabsTrigger>
              <TabsTrigger value="phone">
                <Phone className="mr-2 h-4 w-4" />
                Phone
              </TabsTrigger>
            </TabsList>

            <TabsContent value="email" className="mt-4">
              <form onSubmit={handleEmailSubmit} className="flex flex-col gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">
                    Email <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">
                    Password <span className="text-destructive">*</span>
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      autoComplete="new-password"
                      required
                      minLength={8}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  <p
                    className={cn(
                      "flex items-center gap-1 text-xs",
                      password.length >= 8
                        ? "text-green-600 dark:text-green-400"
                        : "text-muted-foreground",
                    )}
                  >
                    {password.length >= 8 && <Check className="h-3 w-3" />}
                    At least 8 characters.
                  </p>
                </div>
                <Button type="submit" disabled={emailLoading} className="h-11">
                  {emailLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Create account
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="phone" className="mt-4">
              {challenge ? (
                <form onSubmit={handleVerifyCode} className="flex flex-col gap-6">
                  <div className="rounded-2xl border border-border bg-card p-6 text-card-foreground shadow-md">
                    <p className="text-center text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Check your messages
                    </p>
                    <h2 className="mt-2 text-center text-2xl font-bold tracking-tight">
                      Enter verification code
                    </h2>
                    <p className="mt-2 text-center text-sm text-muted-foreground">
                      We sent a code to{" "}
                      <span className="font-medium text-foreground">{phone}</span>
                    </p>
                    <div className="mt-6">
                      <Label htmlFor="signup-code" className="sr-only">
                        Verification code
                      </Label>
                      <SegmentedOtpInput
                        id="signup-code"
                        value={code}
                        onChange={setCode}
                        length={8}
                        disabled={phoneLoading}
                        autoFocus
                        aria-label="SMS verification code"
                      />
                      <p className="mt-3 text-center text-xs text-muted-foreground">
                        You can type each digit or paste the full code.{" "}
                        <button
                          type="button"
                          className="font-medium text-foreground underline-offset-4 hover:underline"
                          onClick={() => {
                            setChallenge(null);
                            setCode("");
                          }}
                        >
                          Use a different number
                        </button>
                      </p>
                    </div>
                  </div>
                  <Button type="submit" disabled={phoneLoading} className="h-11">
                    {phoneLoading && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    Verify and create account
                  </Button>
                </form>
              ) : (
                <form onSubmit={handleSendCode} className="flex flex-col gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="signup-phone">
                      Phone number <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="signup-phone"
                      type="tel"
                      autoComplete="tel"
                      placeholder="+15551234567"
                      required
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">
                      Include your country code, e.g. +1 for the US.
                    </p>
                  </div>
                  <Button type="submit" disabled={phoneLoading} className="h-11">
                    {phoneLoading && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    Send code
                  </Button>
                </form>
              )}
              <div id={recaptchaIdRef.current} className="mt-2" />
            </TabsContent>
          </Tabs>
        </section>

        <div className="flex justify-center pb-4 pt-2">
          <Button variant="ghost" className="h-auto min-w-[50%] text-foreground" asChild>
            <Link
              to="/login"
              className="text-center font-medium"
              {...(redirect ? { search: { redirect } } : {})}
            >
              Already have an account?
            </Link>
          </Button>
        </div>
      </main>
    </div>
  );
}

function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path
        fill="#4285F4"
        d="M23.49 12.27c0-.79-.07-1.54-.2-2.27H12v4.29h6.47a5.53 5.53 0 0 1-2.4 3.63v3h3.88c2.27-2.09 3.54-5.16 3.54-8.65Z"
      />
      <path
        fill="#34A853"
        d="M12 24c3.24 0 5.96-1.07 7.95-2.91l-3.88-3c-1.08.73-2.45 1.17-4.07 1.17-3.13 0-5.78-2.11-6.73-4.96H1.27v3.09A12 12 0 0 0 12 24Z"
      />
      <path
        fill="#FBBC05"
        d="M5.27 14.3A7.2 7.2 0 0 1 4.9 12c0-.8.14-1.58.37-2.3V6.61H1.27A12 12 0 0 0 0 12c0 1.94.46 3.77 1.27 5.39l4-3.09Z"
      />
      <path
        fill="#EA4335"
        d="M12 4.74c1.76 0 3.35.61 4.59 1.8l3.44-3.44C17.95 1.13 15.24 0 12 0A12 12 0 0 0 1.27 6.61l4 3.09c.95-2.85 3.6-4.96 6.73-4.96Z"
      />
    </svg>
  );
}

function AppleIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      aria-hidden="true"
      fill="currentColor"
    >
      <path d="M16.37 1.43c0 1.14-.42 2.22-1.21 3.04-.84.88-2.1 1.55-3.27 1.46-.13-1.13.43-2.32 1.18-3.05.83-.81 2.23-1.42 3.3-1.45ZM20.5 17.05c-.55 1.27-.81 1.83-1.52 2.95-.99 1.55-2.39 3.49-4.13 3.5-1.55.02-1.95-1-4.05-1-2.1.01-2.54 1.02-4.09 1-1.74-.02-3.06-1.77-4.05-3.32C-.18 16.83-.5 11.66 1.84 8.93c1.18-1.39 3.05-2.27 4.85-2.27 1.83 0 2.98 1 4.49 1 1.46 0 2.36-1 4.47-1 1.59 0 3.27.87 4.47 2.36-3.93 2.16-3.29 7.78.38 8.03Z" />
    </svg>
  );
}

